import React from 'react';
import { motion } from 'framer-motion';
import {
  FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaCalendarCheck, FaGift, FaUsers,
  FaCheckCircle, FaRunning, FaTshirt, FaTint, FaSmile, FaBus, FaCalendarAlt,
} from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import Header from './Header';
import Footer from './Footer';
import { SCHEDULE_PATH, TRANSPORT_PATH } from '../config/externalLinks';
import './Enrollment.css';

const PHONE_DISPLAY = '+381 63 113 4892';
const PHONE_TEL = '+381631134892';
const EMAIL = 'info@juniorfk.com';
const MAP_SOKOLSKI = 'https://maps.app.goo.gl/kxhuAwESArRMpWxw9';
const MAP_ZVEZDA = 'https://www.google.com/maps/search/?api=1&query=45.28445,19.83474';

type Step = { title: string; text: string };
type Item = { text: string };
type Faq = { q: string; a: string };

/** Stranica /upis/ — upis otvoren cele godine, prvi treninzi besplatni. */
const Enrollment: React.FC = () => {
  const { t, tList } = useLanguage();
  const steps = tList<Step>('enrollment.steps');
  const bring = tList<Item>('enrollment.bring.items');
  const faq = tList<Faq>('enrollment.faq.items');
  const bringIcons = [<FaRunning />, <FaTshirt />, <FaTint />, <FaSmile />];

  const reveal = (delay = 0) => ({
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.55, delay },
    viewport: { once: true },
  });

  return (
    <div className="App App--cup enroll-page">
      <Header />
      <main>
        <section className="enroll-hero">
          <div className="container">
            <motion.p className="enroll-hero__kicker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
              <FaCalendarCheck aria-hidden /> {t('enrollment.kicker')}
            </motion.p>
            <motion.h1 className="enroll-hero__title" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
              {t('enrollment.title')}
            </motion.h1>
            <motion.p className="enroll-hero__lead" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
              {t('enrollment.lead')}
            </motion.p>
            <motion.div className="enroll-hero__actions" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}>
              <a className="enroll-btn enroll-btn--primary" href={`tel:${PHONE_TEL}`}>
                <FaPhoneAlt aria-hidden /> <span>{t('enrollment.cta.call')} {PHONE_DISPLAY}</span>
              </a>
              <a className="enroll-btn" href={`mailto:${EMAIL}?subject=${encodeURIComponent(t('enrollment.cta.emailSubject'))}`}>
                <FaEnvelope aria-hidden /> <span>{t('enrollment.cta.email')}</span>
              </a>
            </motion.div>
          </div>
        </section>

        <section className="enroll-highlights">
          <div className="container">
            <ul className="enroll-highlights__list">
              <motion.li {...reveal(0)}>
                <FaCalendarCheck className="enroll-highlights__icon" aria-hidden />
                <strong>{t('enrollment.highlights.year.title')}</strong>
                <span>{t('enrollment.highlights.year.text')}</span>
              </motion.li>
              <motion.li {...reveal(0.08)}>
                <FaGift className="enroll-highlights__icon" aria-hidden />
                <strong>{t('enrollment.highlights.free.title')}</strong>
                <span>{t('enrollment.highlights.free.text')}</span>
              </motion.li>
              <motion.li {...reveal(0.16)}>
                <FaUsers className="enroll-highlights__icon" aria-hidden />
                <strong>{t('enrollment.highlights.ages.title')}</strong>
                <span>{t('enrollment.highlights.ages.text')}</span>
              </motion.li>
            </ul>
          </div>
        </section>

        <section className="enroll-steps section-lg">
          <div className="container">
            <motion.h2 className="enroll-section-title" {...reveal()}>{t('enrollment.stepsTitle')}</motion.h2>
            <ol className="enroll-steps__list">
              {steps.map((s, i) => (
                <motion.li key={s.title} className="enroll-steps__item" {...reveal(0.08 * i)}>
                  <span className="enroll-steps__num" aria-hidden>{i + 1}</span>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </motion.li>
              ))}
            </ol>
          </div>
        </section>

        <section className="enroll-info section-lg">
          <div className="container enroll-info__grid">
            <motion.div className="enroll-card" {...reveal()}>
              <h2 className="enroll-card__title">{t('enrollment.bring.title')}</h2>
              <ul className="enroll-card__list">
                {bring.map((b, i) => (
                  <li key={b.text}><span className="enroll-card__icon" aria-hidden>{bringIcons[i] || <FaCheckCircle />}</span>{b.text}</li>
                ))}
              </ul>
            </motion.div>

            <motion.div className="enroll-card" {...reveal(0.1)}>
              <h2 className="enroll-card__title">{t('enrollment.where.title')}</h2>
              <p className="enroll-card__text">{t('enrollment.where.text')}</p>
              <ul className="enroll-card__list enroll-card__list--links">
                <li>
                  <a href={MAP_SOKOLSKI} target="_blank" rel="noopener noreferrer">
                    <FaMapMarkerAlt aria-hidden /> {t('enrollment.where.loc1')}
                  </a>
                </li>
                <li>
                  <a href={MAP_ZVEZDA} target="_blank" rel="noopener noreferrer">
                    <FaMapMarkerAlt aria-hidden /> {t('enrollment.where.loc2')}
                  </a>
                </li>
              </ul>
              <div className="enroll-card__actions">
                <a className="enroll-btn enroll-btn--small" href={SCHEDULE_PATH}>
                  <FaCalendarAlt aria-hidden /> <span>{t('enrollment.where.scheduleBtn')}</span>
                </a>
                <a className="enroll-btn enroll-btn--small" href={TRANSPORT_PATH}>
                  <FaBus aria-hidden /> <span>{t('parents.transport.title')}</span>
                </a>
              </div>
              <p className="enroll-card__note">{t('enrollment.where.note')}</p>
            </motion.div>
          </div>
        </section>

        {faq.length > 0 && (
          <section className="enroll-faq section-lg" aria-labelledby="enroll-faq-title">
            <div className="container">
              <motion.h2 id="enroll-faq-title" className="enroll-section-title" {...reveal()}>
                {t('enrollment.faq.title')}
              </motion.h2>
              <div className="enroll-faq__list">
                {faq.map((f, i) => (
                  <motion.details className="enroll-faq__item" key={f.q} {...reveal(0.04 * i)}>
                    <summary>{f.q}</summary>
                    <p>{f.a}</p>
                  </motion.details>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="enroll-final">
          <div className="container">
            <motion.div className="enroll-final__box" {...reveal()}>
              <h2>{t('enrollment.final.title')}</h2>
              <p>{t('enrollment.final.text')}</p>
              <div className="enroll-hero__actions">
                <a className="enroll-btn enroll-btn--primary" href={`tel:${PHONE_TEL}`}>
                  <FaPhoneAlt aria-hidden /> <span>{PHONE_DISPLAY}</span>
                </a>
                <a className="enroll-btn" href={`mailto:${EMAIL}?subject=${encodeURIComponent(t('enrollment.cta.emailSubject'))}`}>
                  <FaEnvelope aria-hidden /> <span>{EMAIL}</span>
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Enrollment;
